import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import SearchIcon from "@mui/icons-material/Search";
import { Box, IconButton, InputBase, Divider } from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Card from "../../components/common/Card";
import MuiTable from "../../components/common/MuiTable";
import { Column } from "../../utils/constants";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../axios/axios-instance";
import { pathAPI } from "../../utils/pathApi";
import { formatNumber } from "../../utils/utils";
import { orderStatusPillClass, orderStatusLabel, STATUS_PILL_CLASSES } from "../../theme/statusColors";

const ORDER_CURRENCY = "VND";

const compareOrders = (
  a: Order,
  b: Order,
  field: string,
  dir: "asc" | "desc"
) => {
  let result = 0;
  if (field === "created_date") {
    result =
      moment(a.created_date ?? 0).valueOf() -
      moment(b.created_date ?? 0).valueOf();
  } else {
    const key = field as keyof Order;
    result = String(a[key] ?? "").localeCompare(String(b[key] ?? ""), undefined, {
      sensitivity: "base",
    });
  }
  return dir === "asc" ? result : -result;
};

const Order = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loadingTable, setLoadingTable] = useState(false);
  const [data, setDataTable] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [sort, setSort] = useState<{ field: string; dir: "asc" | "desc" }>({
    field: "created_date",
    dir: "desc",
  });

  const getOrderList = async () => {
    setLoadingTable(true);
    await axiosInstance
      .get(pathAPI.orders, { params: { size: 1000 } })
      .then((response: any) => {
        if (response.data) {
          const updatedData = response.data.data.map((item: any) => ({
            ...item,
            action: item.id,
          }));
          setDataTable(updatedData);
        }
        setLoadingTable(false);
      })
      .catch((error: any) => {
        console.log("==== get orders error: ", error);
      });
  };

  useEffect(() => {
    getOrderList();
  }, []);

  const filteredData = data.filter((item: any) => {
    const keyword = searchKeyword.trim();
    if (!keyword) return true;
    if (/^\d+$/.test(keyword)) {
      return String(item.id).includes(keyword);
    }
    return (item.contact_name ?? "")
      .toLowerCase()
      .includes(keyword.toLowerCase());
  });

  const sortedData = [...filteredData].sort((a, b) =>
    compareOrders(a, b, sort.field, sort.dir)
  );

  const handleSort = (field: string) => {
    setSort((prev) => ({
      field,
      dir:
        prev.field === field
          ? prev.dir === "asc"
            ? "desc"
            : "asc"
          : field === "created_date"
          ? "desc"
          : "asc",
    }));
  };

  const header: Column[] = [
    {
      id: "contact_name",
      label: `${t("common.table.customer")}`,
      minWidth: 150,
      align: "center",
      format: (value) => {
        return <div className="font-bold ">{value}</div>;
      },
    },
    {
      id: "total",
      label: `${t("common.table.total_amount")}`,
      minWidth: 100,
      align: "center",
      format: (value) => {
        return (
          <div className="font-bold">
            {formatNumber(value)} {ORDER_CURRENCY}
          </div>
        );
      },
    },
    {
      id: "total_discount",
      label: `${t("common.table.discount_amount")}`,
      minWidth: 100,
      align: "center",
      format: (value) => {
        return (
          <div className="font-bold">
            {formatNumber(value)} {ORDER_CURRENCY}
          </div>
        );
      },
    },
    {
      id: "status",
      label: `${t("common.table.status")}`,
      minWidth: 20,
      align: "center",
      sortable: true,
      format: (value) => {
        return (
          <div
            className={`px-2 rounded-lg font-bold ${orderStatusPillClass(
              value
            )}`}
          >
            {orderStatusLabel(value)}
          </div>
        );
      },
    },
    {
      id: "source",
      label: `${t("common.table.source")}`,
      minWidth: 20,
      align: "center",
      sortable: true,
      format: (value) => {
        return (
          <div
            className={`px-2 rounded-lg font-bold ${
              value === "APP"
                ? STATUS_PILL_CLASSES.SUCCESS
                : STATUS_PILL_CLASSES.DRAFT
            }`}
          >
            {value}
          </div>
        );
      },
    },
    {
      id: "created_date",
      label: `${t("common.table.created_date")}`,
      minWidth: 100,
      align: "center",
      sortable: true,
      format: (value) => {
        return (
          <div>
            {value
              ? moment(value).format("YYYY/MM/DD HH:mm:ss")
              : "-"}
          </div>
        );
      },
    },
    {
      id: "action",
      label: `${t("common.table.actions")}`,
      minWidth: 100,
      align: "center",
      format: (id: number) => {
        return (
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={2}
          >
            <Box
              size={"small"}
              component={IconButton}
              onClick={() => navigate(`${id}`)}
            >
              <RemoveRedEyeIcon color="success" />
            </Box>
          </Box>
        );
      },
    },
  ];

  return (
    <Card>
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", border: "1px solid var(--app-border)", borderRadius: 2, mb: 2, p: 0.5 }}>
          <InputBase
            sx={{ ml: 1, flex: 1 }}
            placeholder="Search orders..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
          />
          <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
          <IconButton type="button" sx={{ p: "10px" }} aria-label="search">
            <SearchIcon />
          </IconButton>
        </Box>
        <MuiTable
          header={header}
          data={sortedData}
          isLoading={loadingTable}
          sortField={sort.field}
          sortDirection={sort.dir}
          onSortChange={handleSort}
        ></MuiTable>
      </Box>
    </Card>
  );
};
export default Order;
